import { gql } from '@apollo/client';
import { useMutation, useMyUrl, useTranslation } from '@gimlite/common';
import { useStoreUIV1, Wizard } from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Mutation,
  MutationCreateFpsArgs,
  Query,
} from '../../../client/graphql';
import { BilanStep } from './steps/bilan/bilan.step';
import { ConfirmStep } from './steps/confirm/confirm.step';
import { InfoStep } from './steps/info/info.step';

export type CreateFPSWizardContext = {
  sessionId: string;
  userId: string;
  lpn: string | null;
  country: string | null;
  countryISO: string | null;
  brand: string | null;
  model: string | null;
  comment: string | null;
  entry: {
    camera: string;
    date: string;
  };
  exit: {
    camera: string;
    date: string;
  };
  confirm: {
    right: boolean;
    claim: boolean;
  };
  claimReasons: string[];
  address: string;
  pictures: Array<{
    mimeType: string;
    description: string;
    pictureDatetime: string;
    contentURL: string;
  }>;
  confirm0: boolean;
  confirm1: boolean;
};

export const CreateFPSWizard = observer(() => {
  const { t } = useTranslation();
  const { SSOStore, WizardStore } = useStoreUIV1();
  const { id } = useParams();
  const { getParamsUrl } = useMyUrl();
  const me = SSOStore.user as Query['me'];
  const { fps } = WizardStore.selected?.store as any;
  const [createFpsCall, createFpsCallState] = useMutation<
    {
      createFps: Mutation['createFps'];
    },
    MutationCreateFpsArgs
  >(gql`
    mutation createFps($input: CreateFpsDto!) {
      createFps(input: $input) {
        fineId
      }
    }
  `);

  const state = useMemo(() => {
    if (createFpsCallState.loading) return undefined;
    if (createFpsCallState.error) return false;
    if (createFpsCallState.data) return true;
  }, [createFpsCallState]);

  return (
    <Wizard<CreateFPSWizardContext>
      config={{
        title: `${t('createFps')} (${fps.lpn})`,
        withValidation: {
          state,
          success: t('CREATE_FPS_SUCCESS'),
          error: t('CREATE_FPS_ERROR'),
        },
      }}
      data={{
        defaultContext: {
          sessionId: fps.sessionId,
          userId: me.userId!,
          lpn: null,
          country: null,
          countryISO: null,
          brand: null,
          model: null,
          comment: null,
          entry: fps.entry,
          exit: fps.exit,
          address: fps.address,
          confirm: fps.confirm,
          claimReasons: [],
          pictures: [
            {
              mimeType: 'image/jpeg',
              description: 'entry',
              pictureDatetime: fps.entry.date,
              contentURL: fps.entry.camera,
            },
            {
              mimeType: 'image/jpeg',
              description: 'exit',
              pictureDatetime: fps.exit.date,
              contentURL: fps.exit.camera,
            },
          ],
          confirm0: false,
          confirm1: false,
        },
      }}
      handleEvent={{
        done: async ({
          sessionId,
          userId,
          lpn,
          countryISO,
          brand,
          model,
          comment,
          pictures,
        }) => {
          await createFpsCall({
            variables: {
              input: {
                sessionId,
                userId,
                licensePlate: {
                  plate: lpn!,
                  plateCountry: countryISO!,
                },
                vehicle: {
                  brand: brand!,
                  model: model || '',
                },
                comment: comment || '',
                pictures,
              },
            },
          });
        },
        clear: () => {},
        close: () => {
          WizardStore.clear();
        },
      }}
    >
      {fps?.confirm && Object.values(fps?.confirm).includes(true) && (
        <Wizard.Item
          config={{ title: t('confirmation'), component: ConfirmStep }}
        />
      )}
      <Wizard.Item config={{ title: t('informations'), component: InfoStep }} />
      <Wizard.Item config={{ title: t('summary'), component: BilanStep }} />
    </Wizard>
  );
});
